import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useAdminTranslation } from '../utils/useAdminTranslation';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { canonicalLinkType, linkTypeLabel, isDofollow } from '../utils/linkType';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  ShoppingBag,
  ExternalLink,
  Calendar,
  FileText,
  Link2,
  Loader2,
  Package,
  XCircle,
  CheckCircle,
  FileCheck,
  Send,
  MessageSquare,
  Edit3,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const translations = {
  es: {
    title: 'Mis Pedidos',
    subtitle: 'Historial de todos tus pedidos realizados',
    back: 'Volver',
    noOrders: 'No tienes pedidos todavía',
    noOrdersDesc: 'Cuando realices un pedido, aparecerá aquí',
    browseCatalog: 'Explorar catálogo',
    orderNumber: 'Pedido #',
    orderDate: 'Fecha del pedido',
    status: 'Estado',
    price: 'Precio',
    details: 'Detalles del pedido',
    website: 'Medio',
    anchor: 'Anchor Text',
    url: 'URL destino',
    priceType: 'Tipo de precio',
    normal: 'Normal',
    sensitive: 'Temática Sensible',
    loading: 'Cargando pedidos...',
    // Nuevos campos
    wordCount: 'Número de palabras',
    internalLink: 'Enlace Interno',
    authorityLink: 'Enlace de Autoridad',
    writerInstructions: 'Instrucciones para el redactor',
    linkType: 'Tipo de enlace',
    doFollow: 'DoFollow',
    noFollow: 'NoFollow',
    writingService: 'Servicio de redacción',
    included: 'Incluido',
    notIncluded: 'No incluido',
    // Fin nuevos campos
    cancelOrder: 'Cancelar Pedido',
    cancelConfirmTitle: '¿Cancelar este pedido?',
    cancelConfirmDescription: 'El importe del pedido será devuelto a tu saldo. Esta acción no se puede deshacer.',
    cancelButton: 'Sí, cancelar pedido',
    keepButton: 'No, mantener pedido',
    cancelSuccess: 'Pedido cancelado correctamente',
    refundMessage: 'Se han devuelto {amount}€ a tu saldo',
    cancelError: 'Error al cancelar el pedido',
    revisionTitle: 'Contenido para Revisión',
    revisionDescription: 'Por favor, revisa el contenido en el siguiente enlace y apruébalo para su publicación.',
    reviewLink: 'Ver Contenido a Revisar',
    approveAndPublish: 'Aprobar y Enviar a Publicar',
    approveConfirmTitle: '¿Aprobar este contenido?',
    approveConfirmDescription: 'Al aprobar, el contenido será enviado para su publicación. Asegúrate de haberlo revisado completamente.',
    approveButton: 'Sí, aprobar',
    approveSuccess: 'Contenido aprobado. Se procederá con la publicación.',
    approveError: 'Error al aprobar el contenido',
    requestModifications: 'Solicitar Modificaciones',
    requestModificationsTitle: 'Solicitar Cambios',
    requestModificationsDesc: 'Indica qué modificaciones necesitas en el contenido',
    modificationsPlaceholder: 'Describe aquí los cambios que necesitas...',
    sendModifications: 'Enviar Solicitud',
    modificationsSuccess: 'Solicitud de modificaciones enviada',
    modificationsError: 'Error al enviar la solicitud',
    modificationsRequired: 'Por favor, describe las modificaciones necesarias',
    needModifications: 'Necesito Modificaciones',
    needModificationsTitle: 'Solicitar Cambios Post-Publicación',
    needModificationsDesc: 'Indica qué cambios necesitas en el contenido publicado',
    needModificationsPlaceholder: 'Describe los cambios que necesitas en la publicación...',
    repeatOrder: 'Repetir Pedido',
    repeatOrderTooltip: 'Crear un nuevo pedido con este mismo medio',
  },
  en: {
    title: 'My Orders',
    subtitle: 'History of all your orders',
    back: 'Back',
    noOrders: 'No orders yet',
    noOrdersDesc: 'When you place an order, it will appear here',
    browseCatalog: 'Browse catalog',
    orderNumber: 'Order #',
    orderDate: 'Order date',
    status: 'Status',
    price: 'Price',
    details: 'Order details',
    website: 'Website',
    anchor: 'Anchor Text',
    url: 'Target URL',
    priceType: 'Price type',
    normal: 'Normal',
    sensitive: 'Sensitive Topic',
    loading: 'Loading orders...',
    // New fields
    wordCount: 'Word count',
    internalLink: 'Internal Link',
    authorityLink: 'Authority Link',
    writerInstructions: 'Writer instructions',
    linkType: 'Link type',
    doFollow: 'DoFollow',
    noFollow: 'NoFollow',
    writingService: 'Writing service',
    included: 'Included',
    notIncluded: 'Not included',
    // End new fields
    cancelOrder: 'Cancel Order',
    cancelConfirmTitle: 'Cancel this order?',
    cancelConfirmDescription: 'The order amount will be refunded to your balance. This action cannot be undone.',
    cancelButton: 'Yes, cancel order',
    keepButton: 'No, keep order',
    cancelSuccess: 'Order cancelled successfully',
    refundMessage: '{amount}€ has been refunded to your balance',
    cancelError: 'Error cancelling order',
    revisionTitle: 'Content for Review',
    revisionDescription: 'Please review the content at the following link and approve it for publication.',
    reviewLink: 'View Content to Review',
    approveAndPublish: 'Approve and Send to Publish',
    approveConfirmTitle: 'Approve this content?',
    approveConfirmDescription: 'By approving, the content will be sent for publication. Make sure you have fully reviewed it.',
    approveButton: 'Yes, approve',
    approveSuccess: 'Content approved. It will be published soon.',
    approveError: 'Error approving content',
    requestModifications: 'Request Modifications',
    requestModificationsTitle: 'Request Changes',
    requestModificationsDesc: 'Indicate what modifications you need in the content',
    modificationsPlaceholder: 'Describe the changes you need here...',
    sendModifications: 'Send Request',
    modificationsSuccess: 'Modification request sent',
    modificationsError: 'Error sending request',
    modificationsRequired: 'Please describe the modifications needed',
    needModifications: 'Need Modifications',
    needModificationsTitle: 'Request Post-Publication Changes',
    needModificationsDesc: 'Indicate what changes you need in the published content',
    needModificationsPlaceholder: 'Describe the changes you need in the publication...',
    repeatOrder: 'Repeat Order',
    repeatOrderTooltip: 'Create a new order with this same media',
  },
  de: {
    title: 'Meine Bestellungen',
    subtitle: 'Verlauf aller Ihrer Bestellungen',
    back: 'Zurück',
    noOrders: 'Noch keine Bestellungen',
    noOrdersDesc: 'Wenn Sie eine Bestellung aufgeben, erscheint sie hier',
    browseCatalog: 'Katalog durchsuchen',
    orderNumber: 'Bestellung #',
    orderDate: 'Bestelldatum',
    status: 'Status',
    price: 'Preis',
    details: 'Bestelldetails',
    website: 'Website',
    anchor: 'Ankertext',
    url: 'Ziel-URL',
    priceType: 'Preistyp',
    normal: 'Normal',
    sensitive: 'Sensibles Thema',
    loading: 'Bestellungen laden...',
    cancelOrder: 'Bestellung stornieren',
    cancelConfirmTitle: 'Diese Bestellung stornieren?',
    cancelConfirmDescription: 'Der Bestellbetrag wird Ihrem Guthaben gutgeschrieben. Diese Aktion kann nicht rückgängig gemacht werden.',
    cancelButton: 'Ja, stornieren',
    keepButton: 'Nein, behalten',
    cancelSuccess: 'Bestellung erfolgreich storniert',
    refundMessage: '{amount}€ wurden Ihrem Guthaben gutgeschrieben',
    cancelError: 'Fehler beim Stornieren',
    revisionTitle: 'Inhalt zur Überprüfung',
    revisionDescription: 'Bitte überprüfen Sie den Inhalt und genehmigen Sie ihn zur Veröffentlichung.',
    reviewLink: 'Inhalt ansehen',
    approveAndPublish: 'Genehmigen und veröffentlichen',
    approveConfirmTitle: 'Diesen Inhalt genehmigen?',
    approveConfirmDescription: 'Mit der Genehmigung wird der Inhalt zur Veröffentlichung gesendet.',
    approveButton: 'Ja, genehmigen',
    approveSuccess: 'Inhalt genehmigt. Wird bald veröffentlicht.',
    approveError: 'Fehler bei der Genehmigung',
    requestModifications: 'Änderungen anfordern',
    requestModificationsTitle: 'Änderungen anfordern',
    requestModificationsDesc: 'Geben Sie an, welche Änderungen Sie benötigen',
    modificationsPlaceholder: 'Beschreiben Sie die benötigten Änderungen...',
    sendModifications: 'Anfrage senden',
    modificationsSuccess: 'Änderungsanfrage gesendet',
    modificationsError: 'Fehler beim Senden',
    modificationsRequired: 'Bitte beschreiben Sie die Änderungen',
    needModifications: 'Änderungen benötigt',
    needModificationsTitle: 'Änderungen nach Veröffentlichung',
    needModificationsDesc: 'Geben Sie an, welche Änderungen Sie benötigen',
    needModificationsPlaceholder: 'Beschreiben Sie die Änderungen...',
  },
  fr: {
    title: 'Mes Commandes',
    subtitle: 'Historique de toutes vos commandes',
    back: 'Retour',
    noOrders: 'Pas encore de commandes',
    noOrdersDesc: 'Lorsque vous passerez une commande, elle apparaîtra ici',
    browseCatalog: 'Parcourir le catalogue',
    orderNumber: 'Commande #',
    orderDate: 'Date de commande',
    status: 'Statut',
    price: 'Prix',
    details: 'Détails de la commande',
    website: 'Site web',
    anchor: 'Texte d\'ancrage',
    url: 'URL cible',
    priceType: 'Type de prix',
    normal: 'Normal',
    sensitive: 'Sujet sensible',
    loading: 'Chargement des commandes...',
    cancelOrder: 'Annuler la commande',
    cancelConfirmTitle: 'Annuler cette commande?',
    cancelConfirmDescription: 'Le montant sera remboursé sur votre solde. Cette action est irréversible.',
    cancelButton: 'Oui, annuler',
    keepButton: 'Non, garder',
    cancelSuccess: 'Commande annulée avec succès',
    refundMessage: '{amount}€ ont été remboursés sur votre solde',
    cancelError: 'Erreur lors de l\'annulation',
    revisionTitle: 'Contenu à réviser',
    revisionDescription: 'Veuillez réviser le contenu et l\'approuver pour publication.',
    reviewLink: 'Voir le contenu',
    approveAndPublish: 'Approuver et publier',
    approveConfirmTitle: 'Approuver ce contenu?',
    approveConfirmDescription: 'En approuvant, le contenu sera envoyé pour publication.',
    approveButton: 'Oui, approuver',
    approveSuccess: 'Contenu approuvé. Publication en cours.',
    approveError: 'Erreur lors de l\'approbation',
    requestModifications: 'Demander des modifications',
    requestModificationsTitle: 'Demander des changements',
    requestModificationsDesc: 'Indiquez les modifications nécessaires',
    modificationsPlaceholder: 'Décrivez les changements nécessaires...',
    sendModifications: 'Envoyer la demande',
    modificationsSuccess: 'Demande de modifications envoyée',
    modificationsError: 'Erreur lors de l\'envoi',
    modificationsRequired: 'Veuillez décrire les modifications',
    needModifications: 'Modifications nécessaires',
    needModificationsTitle: 'Changements post-publication',
    needModificationsDesc: 'Indiquez les changements nécessaires',
    needModificationsPlaceholder: 'Décrivez les changements...',
  },
  it: {
    title: 'I Miei Ordini',
    subtitle: 'Cronologia di tutti i tuoi ordini',
    back: 'Indietro',
    noOrders: 'Nessun ordine ancora',
    noOrdersDesc: 'Quando effettuerai un ordine, apparirà qui',
    browseCatalog: 'Sfoglia il catalogo',
    orderNumber: 'Ordine #',
    orderDate: 'Data ordine',
    status: 'Stato',
    price: 'Prezzo',
    details: 'Dettagli ordine',
    website: 'Sito web',
    anchor: 'Testo ancora',
    url: 'URL di destinazione',
    priceType: 'Tipo di prezzo',
    normal: 'Normale',
    sensitive: 'Argomento sensibile',
    loading: 'Caricamento ordini...',
    cancelOrder: 'Annulla ordine',
    cancelConfirmTitle: 'Annullare questo ordine?',
    cancelConfirmDescription: 'L\'importo sarà rimborsato sul tuo saldo. Questa azione è irreversibile.',
    cancelButton: 'Sì, annulla',
    keepButton: 'No, mantieni',
    cancelSuccess: 'Ordine annullato con successo',
    refundMessage: '{amount}€ sono stati rimborsati sul tuo saldo',
    cancelError: 'Errore durante l\'annullamento',
    revisionTitle: 'Contenuto da revisionare',
    revisionDescription: 'Rivedi il contenuto e approvalo per la pubblicazione.',
    reviewLink: 'Vedi contenuto',
    approveAndPublish: 'Approva e pubblica',
    approveConfirmTitle: 'Approvare questo contenuto?',
    approveConfirmDescription: 'Approvando, il contenuto verrà inviato per la pubblicazione.',
    approveButton: 'Sì, approva',
    approveSuccess: 'Contenuto approvato. Sarà pubblicato a breve.',
    approveError: 'Errore durante l\'approvazione',
    requestModifications: 'Richiedi modifiche',
    requestModificationsTitle: 'Richiedi modifiche',
    requestModificationsDesc: 'Indica le modifiche necessarie',
    modificationsPlaceholder: 'Descrivi le modifiche necessarie...',
    sendModifications: 'Invia richiesta',
    modificationsSuccess: 'Richiesta di modifiche inviata',
    modificationsError: 'Errore durante l\'invio',
    modificationsRequired: 'Descrivi le modifiche necessarie',
    needModifications: 'Modifiche necessarie',
    needModificationsTitle: 'Modifiche post-pubblicazione',
    needModificationsDesc: 'Indica le modifiche necessarie',
    needModificationsPlaceholder: 'Descrivi le modifiche...',
  },
  pt: {
    title: 'Meus Pedidos',
    subtitle: 'Histórico de todos os seus pedidos',
    back: 'Voltar',
    noOrders: 'Nenhum pedido ainda',
    noOrdersDesc: 'Quando você fizer um pedido, ele aparecerá aqui',
    browseCatalog: 'Explorar catálogo',
    orderNumber: 'Pedido #',
    orderDate: 'Data do pedido',
    status: 'Status',
    price: 'Preço',
    details: 'Detalhes do pedido',
    website: 'Site',
    anchor: 'Texto âncora',
    url: 'URL de destino',
    priceType: 'Tipo de preço',
    normal: 'Normal',
    sensitive: 'Tema sensível',
    loading: 'Carregando pedidos...',
    cancelOrder: 'Cancelar Pedido',
    cancelConfirmTitle: 'Cancelar este pedido?',
    cancelConfirmDescription: 'O valor será devolvido ao seu saldo. Esta ação não pode ser desfeita.',
    cancelButton: 'Sim, cancelar',
    keepButton: 'Não, manter',
    cancelSuccess: 'Pedido cancelado com sucesso',
    refundMessage: '{amount}€ foram devolvidos ao seu saldo',
    cancelError: 'Erro ao cancelar pedido',
    revisionTitle: 'Conteúdo para Revisão',
    revisionDescription: 'Por favor, revise o conteúdo e aprove para publicação.',
    reviewLink: 'Ver Conteúdo',
    approveAndPublish: 'Aprovar e Publicar',
    approveConfirmTitle: 'Aprovar este conteúdo?',
    approveConfirmDescription: 'Ao aprovar, o conteúdo será enviado para publicação.',
    approveButton: 'Sim, aprovar',
    approveSuccess: 'Conteúdo aprovado. Será publicado em breve.',
    approveError: 'Erro ao aprovar conteúdo',
    requestModifications: 'Solicitar Modificações',
    requestModificationsTitle: 'Solicitar Alterações',
    requestModificationsDesc: 'Indique as modificações necessárias',
    modificationsPlaceholder: 'Descreva as alterações necessárias...',
    sendModifications: 'Enviar Solicitação',
    modificationsSuccess: 'Solicitação de modificações enviada',
    modificationsError: 'Erro ao enviar solicitação',
    modificationsRequired: 'Por favor, descreva as modificações',
    needModifications: 'Preciso de Modificações',
    needModificationsTitle: 'Alterações Pós-Publicação',
    needModificationsDesc: 'Indique as alterações necessárias',
    needModificationsPlaceholder: 'Descreva as alterações...',
  },
};

const statusColors = {
  'Pendiente de aceptación': 'bg-amber-100 text-amber-700 border-amber-200',
  'Contactando': 'bg-blue-100 text-blue-700 border-blue-200',
  'Redactor': 'bg-indigo-100 text-indigo-700 border-indigo-200',
  'Revisión': 'bg-purple-100 text-purple-700 border-purple-200',
  'Texto Aprobado': 'bg-sky-100 text-sky-700 border-sky-200',
  'Modificaciones Solicitadas': 'bg-orange-100 text-orange-700 border-orange-200',
  'Medio': 'bg-cyan-100 text-cyan-700 border-cyan-200',
  'Publicada': 'bg-green-200 text-green-800 border-green-400',
  'Cancelada': 'bg-red-100 text-red-700 border-red-200',
  'Consultando': 'bg-yellow-100 text-yellow-700 border-yellow-200',
  'Incidencia': 'bg-orange-100 text-orange-700 border-orange-200',
  'Programado': 'bg-teal-100 text-teal-700 border-teal-200',
  'Espera Proforma': 'bg-gray-100 text-gray-700 border-gray-200',
};

const MyOrdersPage = () => {
  const { token } = useAuth();
  const { language, tx } = useAdminTranslation();
  const { addToCart } = useCart();
  const t = translations[language] || translations.es;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [cancellingOrderId, setCancellingOrderId] = useState(null);
  const [approvingOrderId, setApprovingOrderId] = useState(null);
  const [requestingModsOrderId, setRequestingModsOrderId] = useState(null);
  const [showModsForm, setShowModsForm] = useState(null); // Order ID to show form for
  const [modificationsText, setModificationsText] = useState('');
  const [expandedOrderId, setExpandedOrderId] = useState(null); // For auto-expanding accordion

  const fetchOrders = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/my-orders`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        // Ordenar pedidos por número de pedido, más alto primero
        const sortedOrders = (data.orders || []).sort((a, b) => {
          const numA = parseInt(a.order_number, 10) || 0;
          const numB = parseInt(b.order_number, 10) || 0;
          return numB - numA; // Descendente (más alto primero)
        });
        setOrders(sortedOrders);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [token]);
  
  // Handle URL params for auto-opening modification form
  useEffect(() => {
    const action = searchParams.get('action');
    const orderId = searchParams.get('order');
    
    if (action === 'modify' && orderId && orders.length > 0) {
      // Find the order and open the modification form
      const order = orders.find(o => o.id === orderId);
      if (order) {
        setExpandedOrderId(order.id);
        setShowModsForm(order.id);
        // Clear the URL params after handling
        setSearchParams({});
        // Scroll to the order after a small delay
        setTimeout(() => {
          const element = document.getElementById(`order-${order.id}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
      }
    }
  }, [searchParams, orders, setSearchParams]);

  const handleCancelOrder = async (orderId) => {
    setCancellingOrderId(orderId);
    
    try {
      const response = await fetch(`${API_URL}/api/my-orders/${orderId}/cancel`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(t.cancelSuccess, {
          description: t.refundMessage.replace('{amount}', data.refunded_amount?.toFixed(2) || '0'),
        });
        // Refresh orders
        fetchOrders();
      } else {
        const errorData = await response.json();
        toast.error(t.cancelError, {
          description: errorData.detail || 'Unknown error',
        });
      }
    } catch (error) {
      console.error('Error cancelling order:', error);
      toast.error(t.cancelError);
    } finally {
      setCancellingOrderId(null);
    }
  };

  const handleApproveOrder = async (orderId) => {
    setApprovingOrderId(orderId);
    
    try {
      const response = await fetch(`${API_URL}/api/my-orders/${orderId}/approve`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });
      
      if (response.ok) {
        toast.success(t.approveSuccess);
        // Refresh orders
        fetchOrders();
      } else {
        const errorData = await response.json();
        toast.error(t.approveError, {
          description: errorData.detail || 'Unknown error',
        });
      }
    } catch (error) {
      console.error('Error approving order:', error);
      toast.error(t.approveError);
    } finally {
      setApprovingOrderId(null);
    }
  };

  const handleRequestModifications = async (orderId) => {
    if (!modificationsText.trim()) {
      toast.error(t.modificationsRequired);
      return;
    }
    
    setRequestingModsOrderId(orderId);
    
    try {
      const response = await fetch(`${API_URL}/api/my-orders/${orderId}/request-modifications`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ modifications: modificationsText }),
      });
      
      if (response.ok) {
        toast.success(t.modificationsSuccess);
        setShowModsForm(null);
        setModificationsText('');
        // Refresh orders
        fetchOrders();
      } else {
        const errorData = await response.json();
        toast.error(t.modificationsError, {
          description: errorData.detail || 'Unknown error',
        });
      }
    } catch (error) {
      console.error('Error requesting modifications:', error);
      toast.error(t.modificationsError);
    } finally {
      setRequestingModsOrderId(null);
    }
  };

  // Handle repeat order - redirects to cart with product pre-added
  const handleRepeatOrder = async (order) => {
    if (!order.product_url) {
      toast.error(tx('No se puede repetir este pedido', 'Cannot repeat this order'));
      return;
    }

    try {
      // Fetch product info by website URL
      const response = await fetch(`${API_URL}/api/products/by-url?url=${encodeURIComponent(order.product_url)}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const product = await response.json();
        
        // Determine price type based on the original order
        const priceType = order.is_sensitive_order ? 'sensitive' : 'normal';
        
        // Add product to cart
        const success = await addToCart(product, priceType);
        
        if (success) {
          toast.success(tx(
            'Producto añadido al carrito. Completa los datos del nuevo pedido.',
            'Product added to cart. Complete the new order details.'
          ));
          // Navigate to cart page
          navigate('/cart');
        } else {
          // Product might already be in cart
          toast.info(tx(
            'El producto ya está en tu carrito',
            'Product is already in your cart'
          ));
          navigate('/cart');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(tx(
          errorData.detail || 'El producto ya no está disponible',
          errorData.detail || 'Product is no longer available'
        ));
      }
    } catch (error) {
      console.error('Error repeating order:', error);
      toast.error(tx('Error al repetir pedido', 'Error repeating order'));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString(tx('es-ES', 'en-US'), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">{t.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-button">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ShoppingBag className="h-6 w-6 text-primary" />
              {t.title}
            </h1>
            <p className="text-muted-foreground text-sm">{t.subtitle}</p>
          </div>
        </div>

        {/* Orders List */}
        <Card data-testid="orders-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              {t.title}
            </CardTitle>
            <CardDescription>
              {orders.length > 0 
                ? `${orders.length} ${tx('pedido(s) encontrado(s)', 'order(s) found')}`
                : t.noOrdersDesc
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {orders.length === 0 ? (
              <div className="text-center py-12" data-testid="no-orders-message">
                <ShoppingBag className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="text-lg font-medium mb-2">{t.noOrders}</h3>
                <p className="text-muted-foreground text-sm mb-6">{t.noOrdersDesc}</p>
                <Button onClick={() => navigate('/')} data-testid="browse-catalog-btn">
                  {t.browseCatalog}
                </Button>
              </div>
            ) : (
              <Accordion 
                type="single" 
                collapsible 
                className="w-full" 
                data-testid="orders-accordion"
                value={expandedOrderId}
                onValueChange={setExpandedOrderId}
              >
                {orders.map((order, index) => (
                  <AccordionItem 
                    key={order.id} 
                    value={order.id}
                    id={`order-${order.id}`}
                    className="border rounded-lg mb-3 px-4 hover:border-primary/30 transition-colors"
                    data-testid={`order-item-${index}`}
                  >
                    <AccordionTrigger className="hover:no-underline py-4">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 w-full pr-4 text-left">
                        <span className="font-semibold text-base">
                          {t.orderNumber}{order.order_number}
                        </span>
                        <Badge 
                          className={`${statusColors[order.status] || 'bg-gray-100 text-gray-700'} text-xs w-fit`}
                          data-testid={`order-status-${index}`}
                        >
                          {order.status}
                        </Badge>
                        <span className="text-sm text-muted-foreground flex items-center gap-1 sm:ml-auto">
                          <Calendar className="h-3 w-3" />
                          {formatDate(order.order_date)}
                        </span>
                        <span className="font-bold text-primary text-lg">
                          {order.pvp?.toFixed(2)} €
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="pt-2 pb-4 space-y-4 border-t">
                        {/* Website/Product URL */}
                        <div className="flex items-start gap-3 pt-4">
                          <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <div className="flex-1">
                            <p className="text-xs text-muted-foreground mb-1">{t.website}</p>
                            <a 
                              href={order.product_url?.startsWith('http') ? order.product_url : `https://${order.product_url}`}
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-sm font-medium hover:text-primary flex items-center gap-1"
                              data-testid={`order-website-${index}`}
                            >
                              {order.product_url}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                        </div>

                        {/* Anchor Text */}
                        {order.anchor_1 && (
                          <div className="flex items-start gap-3">
                            <Link2 className="h-4 w-4 text-muted-foreground mt-0.5" />
                            <div className="flex-1">
                              <p className="text-xs text-muted-foreground mb-1">{t.anchor}</p>
                              <p className="text-sm font-medium" data-testid={`order-anchor-${index}`}>
                                {order.anchor_1}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Target URL */}
                        {order.url_1 && (
                          <div className="flex items-start gap-3">
                            <ExternalLink className="h-4 w-4 text-muted-foreground mt-0.5" />
                            <div className="flex-1">
                              <p className="text-xs text-muted-foreground mb-1">{t.url}</p>
                              <a 
                                href={order.url_1?.startsWith('http') ? order.url_1 : `https://${order.url_1}`}
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-sm text-primary hover:underline break-all"
                                data-testid={`order-url-${index}`}
                              >
                                {order.url_1}
                              </a>
                            </div>
                          </div>
                        )}

                        {/* Second URL/Anchor if exists */}
                        {order.url_2 && (
                          <div className="flex items-start gap-3">
                            <ExternalLink className="h-4 w-4 text-muted-foreground mt-0.5" />
                            <div className="flex-1">
                              <p className="text-xs text-muted-foreground mb-1">URL 2</p>
                              <a 
                                href={order.url_2?.startsWith('http') ? order.url_2 : `https://${order.url_2}`}
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-sm text-primary hover:underline break-all"
                              >
                                {order.url_2}
                              </a>
                              {order.anchor_2 && (
                                <p className="text-xs text-muted-foreground mt-1">Anchor: {order.anchor_2}</p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Internal Link */}
                        {order.add_internal_link && (
                          <div className="flex items-start gap-3 bg-blue-50 p-2 rounded-lg">
                            <Link2 className="h-4 w-4 text-blue-600 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-xs text-blue-700 font-medium mb-1">🔗 {t.internalLink}</p>
                              {order.internal_link_url && (
                                <a 
                                  href={order.internal_link_url?.startsWith('http') ? order.internal_link_url : `https://${order.internal_link_url}`}
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 hover:underline break-all"
                                >
                                  {order.internal_link_url}
                                </a>
                              )}
                              {order.internal_link_anchor && (
                                <p className="text-xs text-blue-600 mt-1">Anchor: {order.internal_link_anchor}</p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Authority Link */}
                        {order.add_authority_link && (
                          <div className="flex items-start gap-3 bg-amber-50 p-2 rounded-lg">
                            <ExternalLink className="h-4 w-4 text-amber-600 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-xs text-amber-700 font-medium mb-1">🏛️ {t.authorityLink}</p>
                              {order.authority_link_url && (
                                <a 
                                  href={order.authority_link_url?.startsWith('http') ? order.authority_link_url : `https://${order.authority_link_url}`}
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-sm text-amber-600 hover:underline break-all"
                                >
                                  {order.authority_link_url}
                                </a>
                              )}
                              {order.authority_link_anchor && (
                                <p className="text-xs text-amber-600 mt-1">Anchor: {order.authority_link_anchor}</p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Word Count & Writing Cost */}
                        {(order.word_count || order.writing_cost > 0) && (
                          <div className="flex items-start gap-3 bg-purple-50 p-2 rounded-lg">
                            <FileText className="h-4 w-4 text-purple-600 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-xs text-purple-700 font-medium mb-1">✍️ {t.writingService}</p>
                              {order.word_count && (
                                <p className="text-sm text-purple-600">{t.wordCount}: {order.word_count}</p>
                              )}
                              {order.writing_cost > 0 && (
                                <p className="text-sm text-purple-600">{t.price}: {order.writing_cost?.toFixed(2)}€</p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Writer Instructions / Description */}
                        {(order.description || order.writer_instructions) && (
                          <div className="flex items-start gap-3 bg-gray-50 p-2 rounded-lg">
                            <FileText className="h-4 w-4 text-gray-600 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-xs text-gray-700 font-medium mb-1">📋 {t.writerInstructions}</p>
                              <p className="text-sm text-gray-600 whitespace-pre-wrap">
                                {order.writer_instructions || order.description}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Price Type & Link Type */}
                        <div className="flex flex-wrap items-center gap-2 pt-2">
                          <span className="text-xs text-muted-foreground">{t.priceType}:</span>
                          <Badge 
                            variant={order.price_type === 'sensitive' ? 'destructive' : 'secondary'}
                            className="text-xs"
                          >
                            {order.price_type === 'sensitive' ? t.sensitive : t.normal}
                          </Badge>
                          
                          {order.link_type && (() => {
                            const canon = canonicalLinkType(order.link_type);
                            if (!canon) return null;
                            const dof = isDofollow(canon);
                            // Localized fallback for dofollow/nofollow; keep English for sponsored/ugc.
                            const label = (canon === 'dofollow') ? t.doFollow
                                        : (canon === 'nofollow') ? t.noFollow
                                        : linkTypeLabel(canon);
                            return (
                              <>
                                <span className="text-xs text-muted-foreground ml-2">{t.linkType}:</span>
                                <Badge
                                  variant={dof ? 'default' : 'outline'}
                                  className="text-xs"
                                  data-testid={`order-link-type-${index}`}
                                >
                                  {label}
                                </Badge>
                              </>
                            );
                          })()}
                        </div>

                        {/* Published URL if available */}
                        {order.published_url && (
                          <div className="flex items-start gap-3 pt-2 border-t">
                            <div className="flex-1 pt-3">
                              <p className="text-xs text-muted-foreground mb-1">
                                {tx('URL Publicada', 'Published URL')}
                              </p>
                              <a 
                                href={order.published_url?.startsWith('http') ? order.published_url : `https://${order.published_url}`}
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-sm text-green-600 hover:underline flex items-center gap-1"
                              >
                                {order.published_url}
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            </div>
                          </div>
                        )}

                        {/* Sección de modificaciones para pedidos PUBLICADOS */}
                        {order.status === 'Publicada' && (
                          <div className="pt-4 border-t mt-4">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <CheckCircle className="h-5 w-5 text-green-600" />
                                  <span className="text-sm font-medium text-green-700">
                                    {tx('Pedido publicado correctamente', 'Order published successfully')}
                                  </span>
                                </div>
                                {showModsForm !== `pub-${order.id}` && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="border-blue-400 text-blue-700 hover:bg-blue-100"
                                    onClick={() => {
                                      setShowModsForm(`pub-${order.id}`);
                                      setModificationsText('');
                                    }}
                                    data-testid={`need-mods-published-btn-${index}`}
                                  >
                                    <Edit3 className="h-4 w-4 mr-2" />
                                    {t.needModifications}
                                  </Button>
                                )}
                              </div>
                              
                              {/* Formulario de modificaciones para publicados */}
                              {showModsForm === `pub-${order.id}` && (
                                <div className="mt-4 p-4 bg-white border border-blue-200 rounded-lg animate-in fade-in slide-in-from-top-2">
                                  <div className="flex items-center gap-2 mb-3">
                                    <MessageSquare className="h-5 w-5 text-blue-600" />
                                    <h5 className="font-semibold text-blue-800">{t.needModificationsTitle}</h5>
                                  </div>
                                  <p className="text-sm text-blue-700 mb-3">{t.needModificationsDesc}</p>
                                  <Textarea
                                    value={modificationsText}
                                    onChange={(e) => setModificationsText(e.target.value)}
                                    placeholder={t.needModificationsPlaceholder}
                                    className="min-h-[100px] mb-3"
                                    data-testid={`published-mods-textarea-${index}`}
                                  />
                                  <div className="flex gap-2 justify-end">
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => {
                                        setShowModsForm(null);
                                        setModificationsText('');
                                      }}
                                    >
                                      {tx('Cancelar', 'Cancel')}
                                    </Button>
                                    <Button 
                                      className="bg-blue-600 hover:bg-blue-700 text-white"
                                      size="sm"
                                      onClick={() => handleRequestModifications(order.id)}
                                      disabled={requestingModsOrderId === order.id || !modificationsText.trim()}
                                      data-testid={`send-published-mods-btn-${index}`}
                                    >
                                      {requestingModsOrderId === order.id ? (
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                      ) : (
                                        <Send className="h-4 w-4 mr-2" />
                                      )}
                                      {t.sendModifications}
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Sección de revisión - cuando el pedido está en estado Revisión */}
                        {order.status === 'Revisión' && (
                          <div className="pt-4 border-t mt-4">
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                              <div className="flex items-center gap-2 mb-3">
                                <FileCheck className="h-5 w-5 text-purple-600" />
                                <h4 className="font-semibold text-purple-800">{t.revisionTitle}</h4>
                              </div>
                              <p className="text-sm text-purple-700 mb-4">{t.revisionDescription}</p>
                              
                              {/* Paso 1: Revisar el contenido */}
                              <div className="bg-white border border-purple-200 rounded-lg p-4 mb-4">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="bg-purple-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">1</span>
                                  <span className="font-medium text-purple-800">Revisa el contenido</span>
                                </div>
                                {order.revision_url ? (
                                  <div className="flex flex-wrap gap-2">
                                    {order.revision_url.split(' | ').map((item, idx) => {
                                      const trimmed = item.trim();
                                      if (!trimmed) return null;
                                      const isUrl = trimmed.startsWith('http://') || trimmed.startsWith('https://');
                                      const handleClick = async (e) => {
                                        if (isUrl) return; // browser handles http(s) directly
                                        e.preventDefault();
                                        try {
                                          const token = localStorage.getItem('auth_token');
                                          const resp = await fetch(
                                            `${API_URL}/api/orders/${order.id}/files/${encodeURIComponent(trimmed)}`,
                                            { headers: { Authorization: `Bearer ${token}` } }
                                          );
                                          if (!resp.ok) throw new Error('download failed');
                                          const blob = await resp.blob();
                                          const blobUrl = window.URL.createObjectURL(blob);
                                          const a = document.createElement('a');
                                          a.href = blobUrl;
                                          a.download = trimmed;
                                          document.body.appendChild(a);
                                          a.click();
                                          a.remove();
                                          window.URL.revokeObjectURL(blobUrl);
                                        } catch (err) {
                                          toast.error(tx(
                                            'No se pudo descargar el archivo. Contacta con soporte.',
                                            'Could not download the file. Contact support.'
                                          ));
                                        }
                                      };
                                      const label = isUrl
                                        ? (order.revision_url.split(' | ').length > 1 ? `${tx('Enlace', 'Link')} ${idx + 1}` : t.reviewLink)
                                        : (trimmed.length > 32 ? `${tx('Archivo', 'File')} ${idx + 1}` : trimmed);
                                      return (
                                        <a
                                          key={idx}
                                          href={isUrl ? trimmed : '#'}
                                          target={isUrl ? '_blank' : undefined}
                                          rel={isUrl ? 'noopener noreferrer' : undefined}
                                          onClick={handleClick}
                                          className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                                          data-testid={`review-link-${index}-${idx}`}
                                        >
                                          <ExternalLink className="h-4 w-4" />
                                          {label}
                                        </a>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-500 italic">No hay enlace de revisión disponible. Contacta con soporte.</p>
                                )}
                              </div>
                              
                              {/* Paso 2: Aprobar o Solicitar Modificaciones */}
                              <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="bg-gray-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">2</span>
                                  <span className="font-medium text-gray-800">
                                    {tx('Elige una opción', 'Choose an option')}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-500 mb-4">
                                  {tx(
                                    'Si el contenido es correcto, apruébalo. Si necesita cambios, solicita modificaciones.',
                                    'If the content is correct, approve it. If it needs changes, request modifications.'
                                  )}
                                </p>
                                
                                <div className="flex flex-col sm:flex-row gap-3">
                                  {/* Aprobar Button */}
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button 
                                        className="bg-green-600 hover:bg-green-700 text-white flex-1"
                                        size="sm"
                                        disabled={approvingOrderId === order.id || showModsForm === order.id}
                                        data-testid={`approve-order-btn-${index}`}
                                      >
                                        {approvingOrderId === order.id ? (
                                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                        ) : (
                                          <CheckCircle className="h-4 w-4 mr-2" />
                                        )}
                                        {t.approveAndPublish}
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>{t.approveConfirmTitle}</AlertDialogTitle>
                                        <AlertDialogDescription>{t.approveConfirmDescription}</AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>{t.keepButton}</AlertDialogCancel>
                                        <AlertDialogAction 
                                          className="bg-green-600 hover:bg-green-700"
                                          onClick={() => handleApproveOrder(order.id)}
                                        >
                                          {t.approveButton}
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                  
                                  {/* Solicitar Modificaciones Button */}
                                  <Button 
                                    variant="outline"
                                    className="border-amber-500 text-amber-700 hover:bg-amber-50 flex-1"
                                    size="sm"
                                    onClick={() => {
                                      setShowModsForm(showModsForm === order.id ? null : order.id);
                                      setModificationsText('');
                                    }}
                                    disabled={approvingOrderId === order.id}
                                    data-testid={`request-mods-btn-${index}`}
                                  >
                                    <Edit3 className="h-4 w-4 mr-2" />
                                    {t.requestModifications}
                                  </Button>
                                </div>
                                
                                {/* Formulario de modificaciones */}
                                {showModsForm === order.id && (
                                  <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg animate-in fade-in slide-in-from-top-2">
                                    <div className="flex items-center gap-2 mb-3">
                                      <MessageSquare className="h-5 w-5 text-amber-600" />
                                      <h5 className="font-semibold text-amber-800">{t.requestModificationsTitle}</h5>
                                    </div>
                                    <p className="text-sm text-amber-700 mb-3">{t.requestModificationsDesc}</p>
                                    <Textarea
                                      value={modificationsText}
                                      onChange={(e) => setModificationsText(e.target.value)}
                                      placeholder={t.modificationsPlaceholder}
                                      className="min-h-[100px] mb-3 bg-white"
                                      data-testid={`modifications-textarea-${index}`}
                                    />
                                    <div className="flex gap-2 justify-end">
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={() => {
                                          setShowModsForm(null);
                                          setModificationsText('');
                                        }}
                                      >
                                        {tx('Cancelar', 'Cancel')}
                                      </Button>
                                      <Button 
                                        className="bg-amber-600 hover:bg-amber-700 text-white"
                                        size="sm"
                                        onClick={() => handleRequestModifications(order.id)}
                                        disabled={requestingModsOrderId === order.id || !modificationsText.trim()}
                                        data-testid={`send-mods-btn-${index}`}
                                      >
                                        {requestingModsOrderId === order.id ? (
                                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                        ) : (
                                          <Send className="h-4 w-4 mr-2" />
                                        )}
                                        {t.sendModifications}
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Cancel button - only for pending orders */}
                        {order.status === 'Pendiente de aceptación' && (
                          <div className="pt-4 border-t mt-4">
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button 
                                  variant="destructive" 
                                  size="sm"
                                  disabled={cancellingOrderId === order.id}
                                  data-testid={`cancel-order-btn-${index}`}
                                >
                                  {cancellingOrderId === order.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                  ) : (
                                    <XCircle className="h-4 w-4 mr-2" />
                                  )}
                                  {t.cancelOrder}
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>{t.cancelConfirmTitle}</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    {t.cancelConfirmDescription}
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>{t.keepButton}</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleCancelOrder(order.id)}
                                    className="bg-destructive hover:bg-destructive/90"
                                  >
                                    {t.cancelButton}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        )}

                        {/* Repeat Order Button - available for completed or cancelled orders */}
                        {(order.status === 'Publicada' || order.status === 'Cancelada') && order.product_url && (
                          <div className="pt-4 border-t mt-4">
                            <Button 
                              variant="outline"
                              size="sm"
                              onClick={() => handleRepeatOrder(order)}
                              className="border-primary text-primary hover:bg-primary hover:text-white"
                              data-testid={`repeat-order-btn-${index}`}
                              title={t.repeatOrderTooltip}
                            >
                              <RefreshCw className="h-4 w-4 mr-2" />
                              {t.repeatOrder}
                            </Button>
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default MyOrdersPage;
